import {
  BUILDER_DECIDERS,
  BUILDER_GATE_APPROVERS,
  gateOpensWords,
  type GateApprover,
  BUILDER_KINDS,
  BUILDER_KIND_NAMES,
  BUILDER_KIND_WORDS,
  BUILDER_WHO,
  BUILDER_WHO_WORDS,
  bringsTrade,
  builderAdd,
  builderAddOption,
  builderComment,
  builderNest,
  builderRemove,
  builderRemoveOption,
  builderRenameOption,
  builderCompleteness,
  builderUncomment,
  builderUnlink,
  builderSetPhase,
  builderUpdate,
  isStartOrEnd,
  titleCase,
  TRADE_GROUP_WORDS,
  inTradeOrder,
  tradeGroups,
  type BuilderKind,
  type BuilderNode,
  type BuilderTree,
  type BuilderWho,
  type StepIconsDto,
  type TradeDto,
} from '@nsb/core';
import { useMemo, useState, type ReactNode } from 'react';
import { api, errorText, queryClient } from '../../api.ts';
import { Button, Confirm, ErrorLine, Seg, Toggle } from '../ui.tsx';
import { stepIconsKey } from '../process/stepIcons.ts';
import { StepMark } from '../drawings.tsx';
import { CommitText } from './fields.tsx';
import { markOf } from './look.ts';
import { TimeScale } from './TimeScale.tsx';
import { SpecialTasks } from './SpecialTasks.tsx';
import { TaskGroups } from '../process/TaskGroups.tsx';
import { NeedsOpens } from '../process/NeedsOpens.tsx';
import { masterBoard, masterNeedsOpens, masterWalk, nextInWalk, type Side } from '../process/needsOpens.ts';

/**
 * A node in focus, open (design study 03, revision 2): everything about it in
 * one or two clicks (Jason, 2026-10-04: "all adjustments to settings in the
 * node should be a single or 2 clicks, like a radio bar with 'who does it'").
 * What it is and who does or decides it; a sub's or supplier's trade from the
 * key trades; how long, as a range; what it is in words; a decision's options,
 * each a way on, and any node's tasks; what it needs and what it opens
 * (NeedsOpens, design study 31, slice 5); the comments, which
 * Claude reads in the file and answers there. How much of it is said, and what
 * is not yet, across its head.
 */

/** A step named in a line of words: quoted, in Title Case as every step's name is shown (Jason, 2026-10-06). */
const q = (t: string) => `“${titleCase(t)}”`;

function Part({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="mt-4">
      <h3 className="label mb-1.5">{title}</h3>
      {children}
    </section>
  );
}

const small = 'link text-[0.8125rem] text-ink-2 hover:text-ink';

/**
 * What its painted picture shows, set by hand (Jason, 2026-10-08, design study 24: "A roll of protective paper next to
 * a roll of Japanese Kamoi tape"), and Paint again: painted as set, in place of Claude's choice (`domain/step-icons.ts`).
 * The painting reads the words in the draft, so it is asked for only once the page's save holding them is accepted
 * (`kept`, the page's `untilKept`); refused, nothing is painted and the page's line says why the draft was not kept.
 */
export function PictureSubject({
  node,
  painted,
  set,
  kept,
}: {
  node: BuilderNode;
  painted: boolean;
  set: (patch: Parameters<typeof builderUpdate>[2], words: string) => void;
  /** Every change made on the page so far kept in the draft: true once it is, false if its save was refused. */
  kept?: () => Promise<boolean>;
}) {
  const [busy, setBusy] = useState(false);
  const [asked, setAsked] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const paint = async () => {
    setBusy(true);
    setErr(null);
    try {
      // What its picture shows, just set here, kept in the draft first: the painting reads it there.
      if (kept && !(await kept())) {
        setErr('Not painted: the draft was not kept (its line on this page says why). Paint it again once it is.');
        return;
      }
      queryClient.setQueryData(stepIconsKey, await api<StepIconsDto>('/process/step-icons', { body: { keys: [node.key] } }));
      setAsked(true);
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Part title="Its picture">
      <CommitText
        multiline
        rows={2}
        value={node.picture ?? ''}
        maxLength={200}
        aria-label="What its picture shows"
        placeholder="What it shows, in a few words"
        onCommit={(t) => set({ picture: t || null }, t ? `What ${q(node.title)}’s picture shows changed.` : `Claude chooses what ${q(node.title)}’s picture shows again.`)}
      />
      <p className="meta mt-1">{node.picture ? 'Painted as it is set here.' : 'Not set: Claude chooses one thing to paint from its title and what it is.'}</p>
      <div className="mt-1">
        <Button tone="quiet" busy={busy} onClick={() => void paint()}>
          {painted ? 'Paint again' : 'Paint it'}
        </Button>
      </div>
      {asked && !err ? <p className="meta mt-1">Being painted: the new picture takes its place in a minute or so.</p> : null}
      <ErrorLine error={err} />
    </Part>
  );
}

export function NodeCard({
  tree,
  node,
  author,
  picture,
  trades,
  fresh = false,
  edit,
  focusOn,
  place,
  leave,
  kept,
  onChoose,
}: {
  tree: BuilderTree;
  node: BuilderNode;
  /** Just made: its title waits to be written. */
  fresh?: boolean;
  /** Who a comment written here is by. */
  author: string;
  picture: string | null;
  trades: TradeDto[];
  edit: (next: BuilderTree, words: string) => void;
  /** Open another node instead. */
  focusOn: (key: string) => void;
  /** Onto the board, where it is being looked at. */
  place: (key: string) => void;
  leave: () => void;
  /** Every change made on the page so far kept in the draft (the page's `untilKept`): what Paint again waits for. */
  kept?: () => Promise<boolean>;
  /** "+ It needs…" or "+ It opens…": the page chooses the step on the board (TreeView's `choosing`); without it, nothing is added here. */
  onChoose?: (side: Side) => void;
}) {
  const [comment, setComment] = useState('');
  const [optionText, setOptionText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const byKey = useMemo(() => new Map(tree.nodes.map((n) => [n.key, n])), [tree]);
  const title = (k: string) => titleCase(byKey.get(k)?.title ?? k);
  const phaseName = (k: string | null) => tree.phases.find((p) => p.key === k)?.name ?? k ?? 'no phase';
  const completeness = builderCompleteness(tree, node);
  const tasks = tree.nodes.filter((n) => n.parent === node.key);
  // The quick list, then every other trade, each in the order every list of trades is shown in: NSB's own first (core `tradeGroups`).
  const keyTrades = inTradeOrder(trades.filter((t) => t.keyTrade && !t.archived));
  const otherTrades = tradeGroups(trades.filter((t) => !t.keyTrade && !t.archived));
  const set = (patch: Parameters<typeof builderUpdate>[2], words: string) => edit(builderUpdate(tree, node.key, patch), words);
  // What it needs and opens, read with the board as it draws the tree (a line a longer way gives, not drawn on it), and
  // where Next step in the tree goes: the tree's walk, none after the last or from a task in a step's list.
  const board = useMemo(() => masterBoard(tree), [tree]);
  const rows = useMemo(() => masterNeedsOpens(tree, node.key, board), [tree, node.key, board]);
  const walk = useMemo(() => masterWalk(tree), [tree]);
  const nextKey = nextInWalk(walk, node.key);
  /** The two ends of a line from here: what it needs, it waits on; what it opens waits on it. */
  const ends = (side: Side, other: string): [string, string] => (side === 'needs' ? [node.key, other] : [other, node.key]);
  const takeOut = (side: Side, other: string) => {
    const [from, to] = ends(side, other);
    edit(builderUnlink(tree, from, to), `Took out: ${q(title(from))} comes after ${q(title(to))}.`);
  };
  // The start or the end of the process: what it is and its tasks are all there is to say of it, and it stays.
  const fixed = isStartOrEnd(node);

  return (
    <div className="relative w-full bg-sheet">
      <div className="flex items-start gap-4 px-4 pt-3">
        {picture ? <img src={picture} alt="" className="h-20 w-20 shrink-0 object-contain" /> : null}
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-3">
            <span className="label flex items-center gap-1.5">
              <StepMark kind={markOf(node)} state={completeness.configured ? 'ahead' : 'ghost'} size={12} />
              {BUILDER_KIND_WORDS[node.kind] ? `${BUILDER_KIND_WORDS[node.kind]} · opened` : 'Opened'}
            </span>
            <button type="button" className={small} onClick={leave}>
              Back to the whole tree (Esc)
            </button>
          </div>
          <CommitText value={node.title} required autoSelect={fresh} maxLength={200} aria-label="Title" className="!font-display !text-[1.5rem] !leading-tight" onCommit={(t) => set({ title: t }, `Renamed ${q(node.title)} to ${q(t)}.`)} />
          <div className="mt-1.5 flex flex-wrap items-center gap-x-3">
            <span className="inline-block h-[5px] w-32 bg-stone" aria-hidden="true">
              <span className="block h-full bg-moss" style={{ width: `${completeness.of ? Math.round((100 * completeness.done) / completeness.of) : 0}%` }} />
            </span>
            <span className="meta">{completeness.configured ? 'Configured: everything it needs is set.' : `${completeness.done} of ${completeness.of} set · missing: ${completeness.missing.join(', ')}`}</span>
          </div>
        </div>
      </div>

      <div className="grid gap-x-6 px-4 lg:grid-cols-2">
        <div>
          {fixed ? (
            <Part title={node.kind === 'start' ? 'The start' : 'The end'}>
              <p className="text-[0.9375rem]">
                {node.kind === 'start'
                  ? 'Every job begins here, and nothing comes before it: what the first steps wait on. It is nobody’s, takes no time, sits in no phase and stays on the board.'
                  : 'Every job ends here, and nothing comes after it: it waits on the last of the work, whichever way the job went. It is nobody’s, takes no time, sits in no phase and stays on the board.'}
              </p>
            </Part>
          ) : (
            <Part title="Kind">
              <Seg<BuilderKind>
                label="Kind"
                value={node.kind}
                options={BUILDER_KINDS}
                words={(k) => BUILDER_KIND_WORDS[k]}
                names={(k) => BUILDER_KIND_NAMES[k]}
                // On the chosen one's dark ground the mark is filled, so it still shows.
                marks={(k) => <StepMark kind={markOf({ kind: k, who: null })} state={node.kind === k ? 'done' : 'ahead'} size={12} />}
                onPick={(k) => k && set({ kind: k }, k === 'gate' ? `${q(node.title)} holds back what comes after it now.` : `${q(node.title)} is a ${BUILDER_KIND_WORDS[k].toLowerCase()} now.`)}
              />
            </Part>
          )}
          {fixed ? null : node.kind === 'task' ? (
            <Part title="Who does it">
              <Seg<BuilderWho> label="Who does it" value={node.who} options={BUILDER_WHO} words={(w) => BUILDER_WHO_WORDS[w]} onPick={(w) => set({ who: w }, w ? `${q(node.title)}: ${BUILDER_WHO_WORDS[w]} does it now.` : `Who does ${q(node.title)} is blank again.`)} />
            </Part>
          ) : node.kind === 'decision' ? (
            <Part title="Who decides">
              <Seg<BuilderWho> label="Who decides" value={node.who} options={BUILDER_DECIDERS} words={(w) => BUILDER_WHO_WORDS[w]} onPick={(w) => set({ who: w }, w ? `${q(node.title)}: ${BUILDER_WHO_WORDS[w]} decides it now.` : `Who decides ${q(node.title)} is blank again.`)} />
            </Part>
          ) : node.kind === 'gate' ? (
            // How it opens (design study 09, gates A): it happens, or someone approves it; the client presses Approve for
            // theirs, on their home.
            <Part title="How it opens">
              <Seg<GateApprover>
                label="Approved by"
                value={node.who as GateApprover | null}
                options={BUILDER_GATE_APPROVERS}
                words={(w) => (w === 'client' ? 'The client' : w === 'sub' ? 'A sub' : 'NSB')}
                onPick={(w) => set({ who: w }, w ? `${q(node.title)}: ${gateOpensWords(w).toLowerCase()} now.` : `${q(node.title)}: nobody approves it now; it happens.`)}
              />
              <p className="meta mt-1.5">
                {node.who === 'client'
                  ? 'The client approves it on their home once the work reaches it; their approval is on the record.'
                  : node.who
                    ? `${node.who === 'sub' ? 'A sub approves it' : 'NSB approves it'}; NSB marks it done when they have.`
                    : 'Nobody approves it: it happens. Choose who approves it, if someone does.'}
              </p>
            </Part>
          ) : (
            <Part title="Who and how long">
              <p className="meta">Nobody does or signs a milestone: it is reached when all it needs is done.</p>
            </Part>
          )}
          {node.kind === 'task' ? (
            // The authority's holds as two switches on the step (Jason, 2026-10-05: "require permit" and "require
            // inspection" rather than steps of their own), as the long-lead flag holds an order to its gate.
            <Part title="The authority’s holds">
              <Toggle checked={Boolean(node.permit)} onChange={(v) => set({ permit: v }, v ? `${q(node.title)} needs a permit now.` : `${q(node.title)} needs no permit.`)} label="Needs a permit" hint="It cannot start until Permits in hand is passed." />
              <Toggle checked={Boolean(node.inspection)} onChange={(v) => set({ inspection: v }, v ? `${q(node.title)} is inspected now.` : `${q(node.title)} is not inspected.`)} label="Needs an inspection" hint="It is not done until the inspector passes it; a fail is rework on the same step." />
            </Part>
          ) : null}
          {node.kind === 'task' ? (
            // NSB's own hold on anything installed on site, a switch as the permit is (Jason, 2026-10-06).
            <Part title="Before it goes in">
              <Toggle
                checked={Boolean(node.preInstall)}
                onChange={(v) => set({ preInstall: v }, v ? `${q(node.title)} needs a pre-install inspection now.` : `${q(node.title)} needs no pre-install inspection.`)}
                label="Needs a pre-install inspection"
                hint="It cannot start until the site is checked ready for it: measured, and the surfaces right."
              />
            </Part>
          ) : null}
          {node.kind === 'task' || node.kind === 'gate' ? (
            // What it delivers (Jason, 2026-10-06): a design review, opened on the job when the step is added; and an
            // estimate (design study 17, 2026-10-07): a low and a high NSB sets on it, the funnel's band from that day.
            <Part title="What it delivers">
              {node.kind === 'task' ? (
                <Toggle
                  checked={Boolean(node.deliversReview)}
                  onChange={(v) => set({ deliversReview: v }, v ? `${q(node.title)} delivers a design review now.` : `${q(node.title)} delivers no design review.`)}
                  label="A design review"
                  hint="Added to a job, it opens a draft iteration on Design reviews; the client finishing the review marks this step done."
                />
              ) : null}
              <Toggle
                checked={Boolean(node.deliversEstimate)}
                onChange={(v) => set({ deliversEstimate: v }, v ? `${q(node.title)} delivers an estimate now.` : `${q(node.title)} delivers no estimate.`)}
                label="An estimate"
                hint="On a job, NSB sets the job's price on it, a low and a high with a note the client reads: the funnel's band from that day until the next estimate. The step is done once one is set."
              />
            </Part>
          ) : null}
          {/* What it carries from the library of special tasks, and Add a special task while it could take one (spec "Pattern Mapping" §3.2): nothing when neither. */}
          <SpecialTasks tree={tree} node={node} set={set} />
          {node.kind === 'task' || node.kind === 'decision' ? (
            <Part title={node.kind === 'decision' ? 'How long to decide' : 'How long'}>
              <TimeScale label={node.kind === 'decision' ? 'How long to decide' : 'How long it takes'} value={node.time} onChange={(time) => set({ time }, time ? `${q(node.title)} takes a new time.` : `How long ${q(node.title)} takes is blank again.`)} />
            </Part>
          ) : null}
          {fixed ? null : (
          <Part title="Its phase">
            {/* The board lays itself out (design study 09): its phase is chosen here, or by dragging it into another block. */}
            <Seg<string>
              label="Its phase"
              value={node.phase}
              options={tree.phases.map((p) => p.key)}
              words={(k) => tree.phases.find((p) => p.key === k)?.name ?? k}
              onPick={(k) => edit(builderSetPhase(tree, node.key, k), k ? `${q(node.title)} is in ${tree.phases.find((p) => p.key === k)?.name} now.` : `${q(node.title)}’s phase is blank again.`)}
            />
            <p className="meta mt-1.5">Or drag it into another phase’s block on the board.</p>
          </Part>
          )}
          {/* What it needs and what it opens, one step at a time (design study 31, slice 5; §5, A): each line with Take
              out, a line a longer way gives marked, + It needs… and + It opens… choosing on the board (the page's); the
              start needs nothing, and nothing comes after the end. */}
          <div className="mt-4">
            <NeedsOpens
              step={titleCase(node.title)}
              rows={rows}
              only={node.kind === 'start' ? 'opens' : node.kind === 'end' ? 'needs' : undefined}
              onTakeOut={takeOut}
              onAdd={onChoose}
              next={nextKey ? { name: title(nextKey), onGo: () => focusOn(nextKey) } : null}
              onOpen={focusOn}
            />
          </div>
        </div>

        <div>
          <Part title="For NSB">
            <CommitText multiline rows={3} value={node.note ?? ''} maxLength={4000} aria-label="For NSB" onCommit={(t) => set({ note: t || null }, `${q(node.title)}: For NSB changed.`)} />
          </Part>
          {fixed ? null : (
            // What the client reads when they open it (Jason, 2026-10-06): never NSB's note; empty, its tasks only.
            <Part title="For the client">
              <CommitText
                multiline
                rows={3}
                value={node.clientNote ?? ''}
                maxLength={2000}
                aria-label="For the client"
                onCommit={(t) => set({ clientNote: t }, t ? `${q(node.title)}: For the client changed.` : `The client sees ${q(node.title)}’s tasks only.`)}
              />
              <p className="meta mt-1">{typeof node.clientNote !== 'string' ? 'Not set yet.' : node.clientNote ? 'Shown when they open the step.' : 'Nothing: they see its tasks only.'}</p>
            </Part>
          )}
          <PictureSubject node={node} painted={picture !== null} set={set} kept={kept} />
          {node.kind === 'decision' ? (
            <Part title={`Options · ${node.options.length}`}>
              {node.options.length ? (
                <ul className="border-t border-rule">
                  {/* A decision is a decision, not a branch (design study 22): no option is followed, and the funnel follows none. */}
                  {node.options.map((o) => {
                    return (
                      <li key={o.id} className="flex min-h-10 items-center gap-2 border-b border-rule py-1">
                        <span className="h-[9px] w-[9px] shrink-0 rounded-full border border-ink bg-paper" aria-hidden="true" />
                        <CommitText value={o.text} required maxLength={200} aria-label="Option" className="!min-h-9 !border-b-0 !py-1" onCommit={(t) => edit(builderRenameOption(tree, node.key, o.id, t), `Renamed the option ${q(o.text)} to ${q(t)}.`)} />
                        <button type="button" className={`${small} shrink-0`} onClick={() => edit(builderRemoveOption(tree, node.key, o.id), `Took out the option ${q(o.text)}.`)}>
                          Take out
                        </button>
                      </li>
                    );
                  })}
                </ul>
              ) : null}
              <form
                className="mt-1 flex gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  const t = optionText.trim();
                  if (!t) return;
                  edit(builderAddOption(tree, node.key, t).tree, `${q(t)} is an option of ${q(node.title)} now.`);
                  setOptionText('');
                }}
              >
                <input className="field min-w-0 flex-1" value={optionText} onChange={(e) => setOptionText(e.target.value)} placeholder="An option…" aria-label="A new option" maxLength={200} />
                <button type="submit" className={small} disabled={!optionText.trim()}>
                  Add
                </button>
              </form>
              {node.options.length ? (
                // One of many, or many of many (design study 22; Jason, 2026-10-07): on a job, deciding it records the choice.
                <div className="mt-2">
                  <Seg<'one' | 'any'>
                    label="How many are chosen"
                    value={node.choose ?? 'one'}
                    options={['one', 'any']}
                    words={(c) => (c === 'one' ? 'Choose one' : 'Choose any')}
                    keep
                    onPick={(c) => c && set({ choose: c }, `${q(node.title)}: ${c === 'one' ? 'choose one of its options' : 'choose any of its options'} now.`)}
                  />
                </div>
              ) : null}
              <p className="meta mt-1">
                On a job, deciding it records what was chosen: one of these, or any of them. A line drawn from an option’s dot comes after the decision, whatever is chosen.
              </p>
            </Part>
          ) : null}
          <Part title={`Comments · ${node.comments.length}`}>
            {node.comments.map((c) => (
              <div key={c.id} className="border-b border-rule py-1.5">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="label !text-[0.625rem]">
                    {c.by} · {c.at.slice(0, 10)}
                  </span>
                  <button type="button" className={small} onClick={() => edit(builderUncomment(tree, node.key, c.id), `Took a comment off ${q(node.title)}.`)}>
                    Take out
                  </button>
                </div>
                <p className="whitespace-pre-wrap text-[0.9375rem]">{c.text}</p>
              </div>
            ))}
            <form
              className="mt-1"
              onSubmit={(e) => {
                e.preventDefault();
                const text = comment.trim();
                if (!text) return;
                edit(builderComment(tree, node.key, { id: `c-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`, by: author, at: new Date().toISOString(), text }), `A comment on ${q(node.title)}.`);
                setComment('');
              }}
            >
              <textarea className="field" rows={2} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Write a comment…" aria-label="A comment" maxLength={4000} />
              <div className="mt-1 flex justify-end">
                <Button tone="secondary" type="submit" disabled={!comment.trim()}>
                  Add the comment
                </Button>
              </div>
            </form>
          </Part>
        </div>
      </div>

      {/* Under the two columns and across the card (L-38; Jason, 2026-10-30: "make that task thing a full wide in the
          card, not a left column, move the trade selection to the bottom also full wide"): the checklist, then the trades. */}
      <div className="px-4">
        <Part title={`Checklist · ${tasks.length}`}>
          {/* By the phase each is done in, its own last (design study 31, slice 4): a line to type into in each, an
              earlier phase one click away, a task moved to another phase by its Done in or dragged into its group. */}
          <TaskGroups<string, BuilderNode>
            phases={tree.phases}
            own={node.phase}
            tasks={tasks}
            phaseOf={(t) => t.phase ?? node.phase}
            idOf={(t) => t.key}
            nameOf={(t) => titleCase(t.title)}
            listClass="border-t border-rule"
            row={(t, g, drag, doneIn) => (
              <li
                key={t.key}
                {...drag}
                className={`grid min-h-9 items-center gap-2 border-b border-rule py-1 text-[0.9375rem] ${doneIn ? 'grid-cols-[1.4rem_minmax(0,1fr)_auto_auto]' : 'grid-cols-[1.4rem_minmax(0,1fr)_auto]'} ${drag ? 'cursor-grab active:cursor-grabbing' : ''}`}
              >
                <span className="num text-[0.75rem] text-ink-3">{g.tasks.indexOf(t) + 1}</span>
                <button type="button" className="link min-w-0 break-words text-left" onClick={() => focusOn(t.key)}>
                  {titleCase(t.title)}
                </button>
                {doneIn}
                <button type="button" className={small} onClick={() => edit(builderNest(tree, t.key, null), `${q(t.title)} is a step of its own now.`)}>
                  Make it a step
                </button>
              </li>
            )}
            edits={{
              add: (t, phase, own) => {
                edit(builderAdd(tree, { title: t, parent: node.key, phase: phase ?? node.phase }).tree, own ? `${q(t)} is on the checklist of ${q(node.title)}.` : `${q(t)} is on the checklist of ${q(node.title)}, done in ${phaseName(phase)}.`);
                return true;
              },
              move: (key, phase) => edit(builderSetPhase(tree, key, phase), `${q(title(key))} is done in ${phaseName(phase)} now.`),
            }}
          />
        </Part>
        {node.kind === 'task' && bringsTrade(node.who) ? (
          <Part title="Its trade · the key trades">
            <div className="grid grid-cols-[repeat(auto-fill,minmax(7.5rem,1fr))] gap-1.5">
              {keyTrades.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  aria-pressed={node.trade === t.key}
                  onClick={() => set({ trade: node.trade === t.key ? null : t.key }, node.trade === t.key ? `${q(node.title)}’s trade is blank again.` : `${q(node.title)}: ${t.name} now.`)}
                  className={`flex min-h-16 flex-col items-center justify-center gap-0.5 rounded-[2px] border px-1 py-1.5 text-center text-[0.6875rem] leading-tight ${node.trade === t.key ? 'border-[1.5px] border-ink bg-sheet text-ink' : 'border-rule bg-paper text-ink-2 hover:border-ink-3'}`}
                >
                  {t.icon ? <img src={t.icon} alt="" className="h-8 w-8 object-contain" /> : null}
                  <span className="line-clamp-2">{t.name}</span>
                </button>
              ))}
            </div>
            <label className="mt-2 block max-w-md">
              <span className="meta">Or any trade on the list</span>
              <select className="field" value={node.trade && !keyTrades.some((t) => t.key === node.trade) ? node.trade : ''} onChange={(e) => e.target.value && set({ trade: e.target.value }, `${q(node.title)}: ${trades.find((t) => t.key === e.target.value)?.name ?? e.target.value} now.`)}>
                <option value="">Another trade…</option>
                {otherTrades.map((g) => (
                  <optgroup key={g.group} label={TRADE_GROUP_WORDS[g.group]}>
                    {g.trades.map((t) => (
                      <option key={t.key} value={t.key}>
                        {t.csi ? `${t.csi} ` : ''}
                        {t.name}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </label>
          </Part>
        ) : null}
      </div>

      <div className="mx-4 mb-3 mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-rule pt-2">
        {fixed ? (
          <span className="meta">Every tree has it: it stays on the board, and is never deleted.</span>
        ) : node.at !== null ? (
          <span />
        ) : (
          <button type="button" className={small} onClick={() => place(node.key)}>
            {node.parent ? 'Put it on the board as a step of its own' : 'Put it on the board'}
          </button>
        )}
        {fixed ? null : deleting ? (
          <Confirm
            prompt={`Delete ${q(node.title)}? What links to it loses the link.`}
            onYes={() => {
              setDeleting(false);
              edit(builderRemove(tree, node.key), `Deleted ${q(node.title)}.`);
              leave();
            }}
            onNo={() => setDeleting(false)}
          />
        ) : (
          <Button tone="danger" onClick={() => setDeleting(true)}>
            Delete
          </Button>
        )}
      </div>
    </div>
  );
}
